import { logger } from "@amzn/afxtaco-utilities";
import { fetchAuthSession } from "aws-amplify/auth";
import { TacoWebSocket } from "./types";
import { config } from "../../../config";

const PING_INTERVAL = 30 * 1000; // 30 seconds
const PING_MESSAGE = JSON.stringify({ action: "ping" }); // The custom ping message expected by the websocket
interface CreateWebSocketParams {
  onMessageEventHandler: (event: MessageEvent) => void;
}

/**
 * Creates and configures a WebSocket connection with authentication and keep-alive functionality.
 *
 * This function establishes a WebSocket connection to the configured gateway URL, automatically
 * handles authentication via AWS Amplify session tokens, and implements a ping mechanism to
 * prevent idle connection timeouts.
 *
 * @param params - Configuration parameters for the WebSocket
 * @param params.onMessageEventHandler - Callback function invoked when messages are received from the server
 *
 * @returns A Promise that resolves to a TacoWebSocket object containing:
 *   - socket: The underlying WebSocket instance
 *   - sendMessage: A function to send messages through the WebSocket
 *
 * @throws Will throw an error if authentication session cannot be retrieved or token is missing
 *
 * @example
 * ```typescript
 * const ws = await createWebSocket({
 *   onMessageEventHandler: (event) => {
 *     console.log('Received:', event.data);
 *   }
 * });
 *
 * ws.sendMessage('Hello, server!');
 * ```
 *
 * @remarks
 * - The WebSocket automatically sends ping messages every 30 seconds to maintain the connection
 * - Authentication token is passed via the Sec-WebSocket-Protocol header
 * - The connection will automatically clean up the ping interval when closed
 */
export async function createWebSocket(
  params: CreateWebSocketParams,
): Promise<TacoWebSocket> {
  const { onMessageEventHandler } = params;

  const authToken = (await fetchAuthSession()).tokens?.idToken?.toString();

  // Validate that we have an authentication token before creating the WebSocket
  if (!authToken) {
    const error = new Error(
      "Authentication token is missing. User must be authenticated before establishing WebSocket connection.",
    );
    logger.error({
      loggerMsg: "Failed to create WebSocket: missing authentication token",
      error,
    });
    throw error;
  }

  // For WebSockets, we only have access to the Sec-WebSocket-Protocol
  // So we add the authorization token to that header.
  // https://stackoverflow.com/questions/4361173/http-headers-in-websockets-client-api
  const socket = new WebSocket(config.getWssGatewayConfig().url, authToken);
  logger.log({
    loggerMsg: "Websocket created",
  });

  // Buffers non-ping messages sent before the socket reaches OPEN. Without this
  // a prompt submitted during the connection handshake (common on a fresh page
  // load) is silently dropped — the user's request never reaches the backend.
  // Flushed in onopen. Pings are never queued: they're periodic keepalives, so
  // a missed one while connecting is harmless and a stale one is pointless.
  const pendingMessages: string[] = [];

  const sendMessage = (message: string) => {
    if (message !== PING_MESSAGE) {
      logger.log({
        loggerMsg: "Sending message to websocket:",
        message,
      });
    }
    if (socket.readyState === WebSocket.OPEN) {
      socket.send(message);
    } else if (message !== PING_MESSAGE) {
      // Not open yet — queue so the message is delivered once the socket opens,
      // instead of being lost.
      pendingMessages.push(message);
    }
  };

  socket.onmessage = (event: MessageEvent) => {
    const isPong =
      typeof event.data === "string" &&
      (() => {
        try {
          return JSON.parse(event.data)?.type === "pong";
        } catch {
          return false;
        }
      })();
    if (!isPong) {
      logger.log({
        loggerMsg: "Message from server:",
        message: event.data,
      });
    }
    onMessageEventHandler(event);
  };

  let intervalId: NodeJS.Timeout | undefined;

  socket.onopen = () => {
    logger.log({ loggerMsg: "Socket opened" });

    // Flush any messages queued while the socket was still connecting, in the
    // order they were submitted.
    if (pendingMessages.length > 0) {
      logger.log({
        loggerMsg: "Flushing queued websocket messages",
        count: pendingMessages.length,
      });
      while (pendingMessages.length > 0) {
        const queued = pendingMessages.shift();
        if (queued !== undefined) {
          socket.send(queued);
        }
      }
    }

    // Send a message to the websocket server every PING_INTERVAL seconds to prevent
    // websocket connection from timing out due to being idle.
    // https://docs.aws.amazon.com/apigateway/latest/developerguide/apigateway-execution-service-websocket-limits-table.html
    intervalId = setInterval(() => {
      sendMessage(PING_MESSAGE);
    }, PING_INTERVAL);
  };

  socket.onclose = (e) => {
    logger.log({ loggerMsg: "Socket closed", e });
    if (intervalId !== undefined) {
      clearInterval(intervalId);
    }
  };

  socket.onerror = (err) => {
    logger.error({ loggerMsg: "Socket error", err });
    socket.close();
  };

  return {
    socket: socket,
    sendMessage: sendMessage,
  };
}
